import React from 'react';
import { Button } from '../../ui/Button';
import { Upload, FileText, X, Plus } from 'lucide-react';
import { GeoJSONImporterState, GeoJSONImporterActions, GeoJSONLayer } from './GeoJSONImporterPresenter';

interface GeoJSONImporterViewProps {
  state: GeoJSONImporterState;
  actions: GeoJSONImporterActions;
  fileInputRef: React.RefObject<HTMLInputElement>;
  extractPoints: (geoJSON: any) => any[];
  extractLineStrings: (geoJSON: any) => any[];
  layers: GeoJSONLayer[];
  onLayerRemove: (layerId: string) => void;
  onLayerUpdate: (layerId: string, updates: Partial<GeoJSONLayer>) => void;
}

export const GeoJSONImporterView: React.FC<GeoJSONImporterViewProps> = ({
  state,
  actions,
  fileInputRef,
  extractPoints,
  extractLineStrings,
  layers,
  onLayerRemove,
  onLayerUpdate
}) => {
  const { showPointConverter, showLineStringConverter, selectedFile, pointMapping, lineStringMapping, radializeNetwork } = state;
  const {
    handleFileSelect,
    convertPointsToElements,
    convertLineStringsToConnections,
    cancelConversion,
    setPointMapping,
    setLineStringMapping,
    setRadializeNetwork,
    triggerFileInput
  } = actions;

  return (
    <div className="space-y-4">
      {/* Import Button */}
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={triggerFileInput}
          className="w-full flex items-center justify-center space-x-2"
          disabled={showPointConverter}
        >
          <Upload className="h-4 w-4" />
          <span>Import GeoJSON</span>
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".geojson,.json"
          onChange={handleFileSelect}
          className="hidden"
        />
        <label className="mt-2 flex items-start space-x-2 text-xs text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={radializeNetwork}
            onChange={(e) => setRadializeNetwork(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Réseau radial (sans boucle)
            <span className="block text-[11px] text-slate-400">
              Ouvre les boucles à l'import en coupant le câble le plus long de chaque boucle.
              Décochez pour un import fidèle au fichier.
            </span>
          </span>
        </label>
      </div>

      {/* Point Converter Modal */}
      {showPointConverter && selectedFile && (
        <div className="border border-blue-300 bg-blue-50 rounded-lg p-3">
          <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
            <Plus className="h-4 w-4 mr-1" />
            Convertir les points en éléments de grille
          </h4>
          
          <p className="text-xs text-blue-700 mb-3">
            {extractPoints(selectedFile.geoJSON).length} point(s) détecté(s). 
            Choisissez le type d'élément pour chaque point :
          </p>

          <div className="space-y-2 max-h-32 overflow-y-auto mb-3">
            {extractPoints(selectedFile.geoJSON).map((point, index) => {
              const pointId = String(point.id) || `point_${index}`;
              return (
                <div key={pointId} className="flex items-center space-x-2 text-xs">
                  <span className="flex-1 truncate">
                    {String(point.properties?.name) || `Point ${index + 1}`}
                  </span>
                  <select
                    value={pointMapping[pointId] || 'pole'}
                    onChange={(e) => setPointMapping({
                      ...pointMapping,
                      [pointId]: e.target.value
                    })}
                    className="px-1 py-0.5 text-xs border border-slate-300 rounded"
                  >
                    <option value="power-plant">⚡ Power Plant</option>
                    <option value="pole">🔌 Pole</option>
                    <option value="meter">📊 Meter</option>
                    <option value="transformer">🔄 Transformer</option>
                  </select>
                </div>
              );
            })}
          </div>

          <div className="flex space-x-2">
            <Button
              variant="primary"
              size="sm"
              onClick={convertPointsToElements}
              className="flex-1 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Ajouter à la grille
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={cancelConversion}
              className="flex-1 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Ignorer
            </Button>
          </div>
        </div>
      )}

      {/* LineString Converter Modal */}
      {showLineStringConverter && selectedFile && (
        <div className="border border-green-300 bg-green-50 rounded-lg p-3">
          <h4 className="text-sm font-medium text-green-900 mb-2 flex items-center">
            <Plus className="h-4 w-4 mr-1" />
            Convertir les lignes en connexions
          </h4>
          
          <p className="text-xs text-green-700 mb-3">
            {extractLineStrings(selectedFile.geoJSON).length} ligne(s) détectée(s). 
            Choisissez le type de connexion pour chaque ligne :
          </p>

          <div className="space-y-2 max-h-32 overflow-y-auto mb-3">
            {extractLineStrings(selectedFile.geoJSON).map((lineString, index) => {
              const lineStringId = String(lineString.id) || `linestring_${index}`;
              return (
                <div key={lineStringId} className="flex items-center space-x-2 text-xs">
                  <span className="flex-1 truncate">
                    {String(lineString.properties?.name) || `Ligne ${index + 1}`}
                  </span>
                  <select
                    value={lineStringMapping[lineStringId] || 'power-line'}
                    onChange={(e) => setLineStringMapping({
                      ...lineStringMapping,
                      [lineStringId]: e.target.value
                    })}
                    className="px-1 py-0.5 text-xs border border-slate-300 rounded"
                  >
                    <option value="power-line">⚡ Ligne électrique</option>
                    <option value="data-line">📡 Ligne de données</option>
                  </select>
                </div>
              );
            })}
          </div>

          <div className="flex space-x-2">
            <Button
              variant="primary"
              size="sm"
              onClick={convertLineStringsToConnections}
              className="flex-1 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Ajouter les connexions
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={cancelConversion}
              className="flex-1 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Ignorer
            </Button>
          </div>
        </div>
      )}

      {/* Layers List */}
      {layers.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700">Couches GeoJSON</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {layers.map((layer) => (
              <div key={layer.id} className="p-2 bg-slate-50 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-3 w-3 text-slate-500" />
                    <span className="text-xs font-medium text-slate-700 truncate">
                      {layer.name}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onLayerRemove(layer.id)}
                      className="h-5 w-5 p-0"
                    >
                      <X className="h-3 w-3 text-red-600" />
                    </Button>
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
